/**
 * 入力 URL の解析。
 *
 * 原則: 公式の埋め込みに渡せる識別子 (videoId / channelId / channel login /
 * VOD id) を取り出すだけ。ページの HTML を取得して解析する、ストリーム URL を
 * 抽出するといった非公式な手段は一切使わない。
 */

export type Source =
  | { provider: 'youtube'; kind: 'video'; videoId: string }
  | { provider: 'youtube'; kind: 'channelLive'; channelId: string }
  | { provider: 'twitch'; kind: 'channel'; channel: string }
  | { provider: 'twitch'; kind: 'video'; videoId: string };

export interface ParseResult {
  source: Source | null;
  error?: string;
}

const YT_ID = /^[\w-]{11}$/;
const YT_CHANNEL_ID = /^UC[\w-]{22}$/;
const TWITCH_LOGIN = /^[a-zA-Z0-9_]{1,25}$/;
const TWITCH_VIDEO = /^\d{1,15}$/;

/** URL でなく素の ID / チャンネル名を貼られた場合も受け付ける。 */
function bare(input: string): Source | null {
  if (YT_ID.test(input)) return { provider: 'youtube', kind: 'video', videoId: input };
  if (YT_CHANNEL_ID.test(input)) return { provider: 'youtube', kind: 'channelLive', channelId: input };
  return null;
}

export function parseSourceUrl(raw: string): ParseResult {
  const input = raw.trim();
  if (!input) return { source: null, error: 'URL を入力してください' };

  const asBare = bare(input);
  if (asBare) return { source: asBare };

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`);
  } catch {
    return { source: null, error: 'URL として解釈できません' };
  }

  // 明示的に https のみ許可する (javascript: / data: などを排除)。
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { source: null, error: 'https の URL のみ対応しています' };
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
  const segments = url.pathname.split('/').filter(Boolean);

  // ---- YouTube ----
  if (host === 'youtu.be') {
    const id = segments[0] ?? '';
    return YT_ID.test(id)
      ? { source: { provider: 'youtube', kind: 'video', videoId: id } }
      : { source: null, error: 'youtu.be の動画 ID を認識できません' };
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'music.youtube.com') {
    const v = url.searchParams.get('v');
    if (v && YT_ID.test(v)) return { source: { provider: 'youtube', kind: 'video', videoId: v } };

    const [first, second] = segments;

    // /live/ID, /embed/ID, /shorts/ID, /v/ID
    if (first && ['live', 'embed', 'shorts', 'v'].includes(first) && second) {
      if (second === 'live_stream') {
        const ch = url.searchParams.get('channel');
        if (ch && YT_CHANNEL_ID.test(ch)) {
          return { source: { provider: 'youtube', kind: 'channelLive', channelId: ch } };
        }
      }
      if (YT_ID.test(second)) return { source: { provider: 'youtube', kind: 'video', videoId: second } };
    }

    // /channel/UCxxxx (/live)
    if (first === 'channel' && second && YT_CHANNEL_ID.test(second)) {
      return { source: { provider: 'youtube', kind: 'channelLive', channelId: second } };
    }

    // /@handle, /user/name, /c/name は「名前」でしかなく、公式の live_stream 埋め込みは
    // channelId (UC…) を要求する。名前から channelId を引くには YouTube Data API が必要で、
    // そのためだけに OAuth とサーバを持つのをやめたので非対応にした
    // (HTML を取得して解析する方法は非公式なので採らない)。代わりの貼り方を案内する。
    if (
      (first?.startsWith('@') && first.length > 1) ||
      ((first === 'user' || first === 'c') && second)
    ) {
      return {
        source: null,
        error: 'チャンネル名の URL は非対応です。配信ページ (watch?v=…) か youtube.com/channel/UC… を貼ってください',
      };
    }

    return { source: null, error: 'YouTube の URL から動画/チャンネルを特定できません' };
  }

  // ---- Twitch ----
  const twitchHosts = ['twitch.tv', 'player.twitch.tv', 'embed.twitch.tv', 'clips.twitch.tv'];
  if (twitchHosts.includes(host)) {
    // player.twitch.tv/?channel=xxx / ?video=vNNN
    const qChannel = url.searchParams.get('channel');
    if (qChannel && TWITCH_LOGIN.test(qChannel)) {
      return { source: { provider: 'twitch', kind: 'channel', channel: qChannel.toLowerCase() } };
    }
    const qVideo = url.searchParams.get('video');
    if (qVideo) {
      const id = qVideo.replace(/^v/, '');
      if (TWITCH_VIDEO.test(id)) return { source: { provider: 'twitch', kind: 'video', videoId: id } };
    }

    const [first, second, third] = segments;

    // /videos/123456789
    if (first === 'videos' && second && TWITCH_VIDEO.test(second)) {
      return { source: { provider: 'twitch', kind: 'video', videoId: second } };
    }
    // /channel/video/123456789
    if (first && second === 'video' && third && TWITCH_VIDEO.test(third)) {
      return { source: { provider: 'twitch', kind: 'video', videoId: third } };
    }
    // クリップは埋め込み仕様が別 (clips.twitch.tv) なので現状は非対応と明示する。
    if (second === 'clip' || host === 'clips.twitch.tv') {
      return { source: null, error: 'Twitch のクリップは未対応です' };
    }
    // /channelname
    if (first && TWITCH_LOGIN.test(first) && !['directory', 'settings', 'downloads'].includes(first)) {
      return { source: { provider: 'twitch', kind: 'channel', channel: first.toLowerCase() } };
    }

    return { source: null, error: 'Twitch の URL からチャンネル/動画を特定できません' };
  }

  return { source: null, error: 'YouTube と Twitch の URL のみ対応しています' };
}

export function sourceKey(s: Source): string {
  switch (s.kind) {
    case 'video':
      return s.provider === 'youtube' ? `yt:v:${s.videoId}` : `tw:v:${s.videoId}`;
    case 'channelLive':
      return `yt:c:${s.channelId}`;
    case 'channel':
      return `tw:c:${s.channel}`;
  }
}

export function defaultTitle(s: Source): string {
  switch (s.kind) {
    case 'video':
      return s.provider === 'youtube' ? `YouTube ${s.videoId}` : `Twitch VOD ${s.videoId}`;
    case 'channelLive':
      return `YouTube Live ${s.channelId}`;
    case 'channel':
      return `Twitch ${s.channel}`;
  }
}
